import { useState, useRef } from "react";
import { clientPortalApi } from "@/services/api";

interface SiteVisitFormProps {
  clientName: string;
  clientCompany: string;
  jobNo: string;
  jobTitle: string;
  companyEmail: string;
  companyPhone: string;
}

const SERVICE_TYPES = [
  "Topographic Survey",
  "DGPS Survey",
  "Boundary Survey",
  "Drone Survey",
  "Revenue Survey",
  "As-Built Survey",
  "Soil Test",
  "Hydrogeological Survey",
  "Other",
];

const TIME_SLOTS = [
  "08:00 AM – 10:00 AM",
  "10:00 AM – 12:00 PM",
  "12:00 PM – 02:00 PM",
  "02:00 PM – 04:00 PM",
  "04:00 PM – 06:00 PM",
];

type FormStatus = "idle" | "submitting" | "success" | "error";

export default function SiteVisitForm({
  clientName,
  clientCompany,
  jobNo,
  jobTitle,
  companyEmail,
  companyPhone,
}: SiteVisitFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<FormStatus>("idle");
  const [charCount, setCharCount] = useState(0);
  const [form, setForm] = useState({
    contactName: clientName,
    email: "",
    phone: "",
    company: clientCompany,
    jobReference: jobNo,
    serviceType: "",
    visitDate: "",
    alternateDate: "",
    preferredTime: "",
    siteAddress: "",
    purpose: "",
    attendees: "1",
    specialRequirements: "",
  });

  const today = new Date().toISOString().split("T")[0];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "specialRequirements") setCharCount(value.length);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (charCount > 500) return;
    setStatus("submitting");
    try {
      await clientPortalApi.submitSiteVisit(form as unknown as Record<string, string>);
      setStatus("success");
    } catch {
      // Fallback to Readdy form if backend unavailable
      try {
        const body = new URLSearchParams();
        Object.entries(form).forEach(([k, v]) => body.append(k, v));
        const res = await fetch("https://readdy.ai/api/form/d7am6r641prrpukdf9s0", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        });
        if (res.ok) { setStatus("success"); } else { setStatus("error"); }
      } catch { setStatus("error"); }
    }
  };

  if (status === "success") {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-calendar-check-line text-3xl text-amber-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Visit Request Submitted!</h3>
        <p className="text-sm text-gray-500 leading-relaxed max-w-sm mx-auto mb-6">
          Your site visit request has been received. Our team will confirm the appointment within 24 hours via email or phone.
        </p>
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 max-w-xs mx-auto mb-6">
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Job Ref</span>
              <span className="font-mono font-semibold text-gray-800 text-xs">{form.jobReference}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Preferred Date</span>
              <span className="font-semibold text-gray-800">{form.visitDate}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Time Slot</span>
              <span className="font-semibold text-gray-800">{form.preferredTime || "—"}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center gap-3 text-xs text-gray-400">
          <span>Questions?</span>
          <a href={`mailto:${companyEmail}`} className="text-amber-600 hover:underline font-medium">{companyEmail}</a>
          <span>·</span>
          <span className="font-medium text-gray-600">{companyPhone}</span>
        </div>
        <button
          onClick={() => { setStatus("idle"); setForm((p) => ({ ...p, visitDate: "", alternateDate: "", preferredTime: "", specialRequirements: "" })); setCharCount(0); }}
          className="mt-6 px-5 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap"
        >
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="bg-teal-50 border border-teal-100 rounded-xl p-4 flex items-start gap-3">
        <span className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
          <i className="ri-map-pin-2-line text-teal-600" />
        </span>
        <div>
          <p className="text-sm font-semibold text-teal-800">Request a Site Visit</p>
          <p className="text-xs text-teal-700 mt-0.5 leading-relaxed">
            Fill in the form below to schedule a site visit with our survey team. We&apos;ll confirm your appointment within 24 hours.
          </p>
        </div>
      </div>

      <form
        ref={formRef}
        onSubmit={handleSubmit}
        data-readdy-form
        className="bg-white rounded-xl border border-gray-100 overflow-hidden"
      >
        {/* Section: Contact Details */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-user-3-line text-gray-500 text-sm" /></span>
            Contact Details
          </h3>
        </div>
        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Full Name *</label>
            <input
              type="text"
              name="contactName"
              required
              value={form.contactName}
              onChange={handleChange}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Email Address *</label>
            <input
              type="email"
              name="email"
              required
              value={form.email}
              onChange={handleChange}
              placeholder="your@email.com"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Phone Number *</label>
            <input
              type="tel"
              name="phone"
              required
              value={form.phone}
              onChange={handleChange}
              placeholder="+91 XXXXX XXXXX"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Company / Organisation</label>
            <input
              type="text"
              name="company"
              value={form.company}
              onChange={handleChange}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
            />
          </div>
        </div>

        {/* Section: Visit Details */}
        <div className="px-6 py-4 border-t border-b border-gray-100 bg-gray-50">
          <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-calendar-event-line text-gray-500 text-sm" /></span>
            Visit Details
          </h3>
        </div>
        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Job Reference</label>
            <input
              type="text"
              name="jobReference"
              value={form.jobReference}
              onChange={handleChange}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-gray-50 font-mono focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Service Type *</label>
            <select
              name="serviceType"
              required
              value={form.serviceType}
              onChange={handleChange}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all bg-white"
            >
              <option value="">Select service type...</option>
              {SERVICE_TYPES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Preferred Visit Date *</label>
            <input
              type="date"
              name="visitDate"
              required
              min={today}
              value={form.visitDate}
              onChange={handleChange}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Alternate Date</label>
            <input
              type="date"
              name="alternateDate"
              min={today}
              value={form.alternateDate}
              onChange={handleChange}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Preferred Time Slot</label>
            <select
              name="preferredTime"
              value={form.preferredTime}
              onChange={handleChange}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all bg-white"
            >
              <option value="">Any time</option>
              {TIME_SLOTS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">No. of Attendees</label>
            <input
              type="number"
              name="attendees"
              min="1"
              max="20"
              value={form.attendees}
              onChange={handleChange}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Site Address / Location *</label>
            <input
              type="text"
              name="siteAddress"
              required
              value={form.siteAddress}
              onChange={handleChange}
              placeholder="Full address or landmark of the survey site"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Purpose of Visit *</label>
            <input
              type="text"
              name="purpose"
              required
              value={form.purpose}
              onChange={handleChange}
              placeholder="e.g. Initial site inspection, boundary verification, progress review..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
              Special Requirements
              <span className="ml-2 text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              name="specialRequirements"
              value={form.specialRequirements}
              onChange={handleChange}
              rows={3}
              maxLength={500}
              placeholder="Any access restrictions, equipment needed, safety requirements, etc."
              className={`w-full border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 resize-none transition-all ${charCount > 500 ? "border-red-300 focus:border-red-400" : "border-gray-200 focus:border-amber-400"}`}
            />
            <div className="flex justify-between mt-1">
              {charCount > 500 && <p className="text-xs text-red-500">Exceeds 500 character limit</p>}
              <p className={`text-xs ml-auto ${charCount > 500 ? "text-red-500" : "text-gray-400"}`}>{charCount}/500</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-4">
          <p className="text-xs text-gray-400 leading-relaxed">
            Our team will contact you within <strong>24 hours</strong> to confirm the appointment.
          </p>
          <div className="flex items-center gap-3 flex-shrink-0">
            {status === "error" && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <i className="ri-error-warning-line" />Submission failed. Try again.
              </p>
            )}
            <button
              type="submit"
              disabled={status === "submitting" || charCount > 500}
              className="flex items-center gap-2 px-6 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-bold cursor-pointer hover:bg-amber-700 transition-all whitespace-nowrap disabled:opacity-60"
            >
              {status === "submitting" ? (
                <><i className="ri-loader-4-line animate-spin" />Submitting...</>
              ) : (
                <><i className="ri-calendar-check-line" />Request Site Visit</>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Contact info */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 flex items-center gap-4">
        <span className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-xl flex-shrink-0">
          <i className="ri-phone-line text-gray-500 text-lg" />
        </span>
        <div>
          <p className="text-sm font-semibold text-gray-800">Prefer to call?</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Reach us at <strong className="text-gray-700">{companyPhone}</strong> or email{" "}
            <a href={`mailto:${companyEmail}`} className="text-amber-600 hover:underline font-medium">{companyEmail}</a>
          </p>
        </div>
      </div>
    </div>
  );
}
